/**
 * Feature Tooltip Component
 *
 * Contextual tooltip that appears for first-time feature interactions.
 * Uses centralized context to ensure only ONE tooltip is visible at a time.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Icons } from '../Icons';
import { useOptionalFeatureDiscovery } from '../../contexts/FeatureDiscoveryContext';
import { type FeatureId, FEATURE_INFO } from '../../hooks/useFeatureDiscovery';
import './FeatureTooltip.css';

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right' | 'auto';

interface FeatureTooltipProps {
  /** The feature this tooltip describes */
  featureId: FeatureId;
  /** The element the tooltip is attached to */
  children: React.ReactNode;
  /** Preferred position (will auto-adjust if not enough space) */
  position?: TooltipPosition;
  /** Show tooltip on hover instead of automatically */
  showOnHover?: boolean;
  /** Delay before showing (ms) */
  delay?: number;
  /** Custom offset from the target element */
  offset?: number;
  /** Callback when tooltip is dismissed */
  onDismiss?: () => void;
  /** Disable the tooltip entirely */
  disabled?: boolean;
}

export function FeatureTooltip({
  featureId,
  children,
  position = 'auto',
  showOnHover = false,
  delay = 800,
  offset = 8,
  onDismiss,
  disabled = false,
}: FeatureTooltipProps) {
  const [calculatedPosition, setCalculatedPosition] = useState<Exclude<TooltipPosition, 'auto'>>('bottom');
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const targetRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<number | null>(null);
  const hasRequestedRef = useRef(false);

  // Use context for centralized tooltip management
  const featureDiscovery = useOptionalFeatureDiscovery();

  // Get feature info
  const featureInfo = FEATURE_INFO[featureId];

  // Check if this tooltip should be visible (context controls singleton)
  const isVisible = featureDiscovery?.activeTooltip === featureId;
  const isFirstTime = featureDiscovery?.isFirstTime(featureId) ?? false;
  const areTooltipsDisabled = featureDiscovery?.areTooltipsDisabled ?? false;
  const hasCompletedOnboarding = featureDiscovery?.hasCompletedOnboarding ?? false;

  // Should we show the pulsing indicator?
  const showIndicator = !disabled && !areTooltipsDisabled && !hasCompletedOnboarding && isFirstTime && !showOnHover && !!featureInfo;

  // Calculate optimal position
  const calculatePosition = useCallback(() => {
    if (!targetRef.current || !tooltipRef.current) return;

    const targetRect = targetRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let finalPosition = position;

    // Auto-calculate best position
    if (position === 'auto') {
      const spaceAbove = targetRect.top;
      const spaceBelow = viewportHeight - targetRect.bottom;
      const spaceLeft = targetRect.left;
      const spaceRight = viewportWidth - targetRect.right;

      if (spaceBelow >= tooltipRect.height + offset) {
        finalPosition = 'bottom';
      } else if (spaceAbove >= tooltipRect.height + offset) {
        finalPosition = 'top';
      } else if (spaceRight >= tooltipRect.width + offset) {
        finalPosition = 'right';
      } else {
        finalPosition = 'left';
      }
    }

    setCalculatedPosition(finalPosition as Exclude<TooltipPosition, 'auto'>);

    // Calculate coordinates
    let top = 0;
    let left = 0;

    switch (finalPosition) {
      case 'top':
        top = targetRect.top - tooltipRect.height - offset;
        left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
        break;
      case 'bottom':
        top = targetRect.bottom + offset;
        left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
        break;
      case 'left':
        top = targetRect.top + (targetRect.height - tooltipRect.height) / 2;
        left = targetRect.left - tooltipRect.width - offset;
        break;
      case 'right':
        top = targetRect.top + (targetRect.height - tooltipRect.height) / 2;
        left = targetRect.right + offset;
        break;
    }

    // Keep within viewport
    left = Math.max(8, Math.min(left, viewportWidth - tooltipRect.width - 8));
    top = Math.max(8, Math.min(top, viewportHeight - tooltipRect.height - 8));

    setCoords({ top, left });
  }, [position, offset]);

  // Request tooltip after delay (only once per mount)
  useEffect(() => {
    if (disabled || showOnHover || areTooltipsDisabled || hasCompletedOnboarding || !isFirstTime) {
      return;
    }

    // Only request once per component mount
    if (hasRequestedRef.current) return;

    timeoutRef.current = window.setTimeout(() => {
      hasRequestedRef.current = true;
      featureDiscovery?.requestTooltip(featureId);
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [featureId, disabled, showOnHover, delay, areTooltipsDisabled, hasCompletedOnboarding, isFirstTime, featureDiscovery]);

  // Recalculate position when visible
  useEffect(() => {
    if (isVisible) {
      // Small delay to let DOM update
      requestAnimationFrame(() => {
        calculatePosition();
      });
      window.addEventListener('resize', calculatePosition);
      window.addEventListener('scroll', calculatePosition, true);
    }

    return () => {
      window.removeEventListener('resize', calculatePosition);
      window.removeEventListener('scroll', calculatePosition, true);
    };
  }, [isVisible, calculatePosition]);

  const handleDismiss = useCallback(() => {
    featureDiscovery?.dismissActiveTooltip();
    onDismiss?.();
  }, [featureDiscovery, onDismiss]);

  // Don't render anything special if no context or disabled
  if (!featureDiscovery || disabled) {
    return <>{children}</>;
  }

  return (
    <>
      <div
        ref={targetRef}
        className="feature-tooltip-target"
        style={{ position: 'relative', display: 'inline-block' }}
      >
        {children}
        {/* Pulsing indicator for new features */}
        {showIndicator && (
          <span className="feature-tooltip-indicator" aria-hidden="true" />
        )}
      </div>

      {isVisible && featureInfo && createPortal(
        <div
          ref={tooltipRef}
          className={`feature-tooltip feature-tooltip-${calculatedPosition}`}
          style={{
            top: coords.top,
            left: coords.left,
          }}
          role="tooltip"
          aria-live="polite"
        >
          {/* Arrow */}
          <div className={`feature-tooltip-arrow feature-tooltip-arrow-${calculatedPosition}`} />

          {/* Header */}
          <div className="feature-tooltip-header">
            <div className="feature-tooltip-badge">
              <Icons.Sparkles size={12} />
              Nouveau
            </div>
            <button
              className="feature-tooltip-close"
              onClick={handleDismiss}
              aria-label="Fermer"
            >
              <Icons.X size={14} />
            </button>
          </div>

          {/* Content */}
          <div className="feature-tooltip-content">
            <h4 className="feature-tooltip-title">{featureInfo.title}</h4>
            <p className="feature-tooltip-description">{featureInfo.description}</p>

            {/* Tips */}
            {featureInfo.tips && featureInfo.tips.length > 0 && (
              <ul className="feature-tooltip-tips">
                {featureInfo.tips.map((tip, index) => (
                  <li key={index}>
                    <Icons.Lightbulb size={12} />
                    {tip}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="feature-tooltip-footer">
            <button
              className="feature-tooltip-button"
              onClick={handleDismiss}
            >
              Compris !
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

/**
 * Simple tooltip wrapper for showing hint on hover
 * Use this for features already discovered but still want hint available
 */
export function HintTooltip({
  featureId,
  children,
  position = 'top',
}: {
  featureId: FeatureId;
  children: React.ReactNode;
  position?: TooltipPosition;
}) {
  return (
    <FeatureTooltip
      featureId={featureId}
      showOnHover
      position={position}
    >
      {children}
    </FeatureTooltip>
  );
}
